import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, LightbulbIcon, GlobeIcon, BookOpenIcon, AwardIcon, BriefcaseIcon } from 'lucide-react';
export function Solutions() {
  const solutions = [{
    title: 'Meetings & Retreats',
    description: 'Strategic gatherings that spark innovation and alignment while providing a refreshing change of environment.',
    outcomes: ['Increased team alignment', 'Accelerated decision making', 'Enhanced collaboration', 'Improved employee engagement'],
    activities: ['Strategy sprints', 'Executive cabanas', 'Beachside breakouts', 'Sunrise wellness sessions'],
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Incentives',
    description: 'Reward top performers with unforgettable experiences that inspire future success and drive motivation.',
    outcomes: ['Boosted team morale', 'Increased retention', 'Motivation for future performance', 'Strengthened company culture'],
    activities: ['Luxury excursions', 'VIP experiences', 'Gala dinners', 'Adventure activities'],
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Brand Launches',
    description: 'Create buzz and alignment around your new product or initiative with immersive brand experiences.',
    outcomes: ['Consistent messaging', 'Enthusiastic brand advocates', 'Memorable product experiences', 'Media-worthy moments'],
    activities: ['Immersive product demos', 'Media moments', 'Training workshops', 'Brand activation experiences'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Hybrid/Virtual Enhancements',
    description: 'Extend your in-person event to remote participants with engaging digital experiences.',
    outcomes: ['Inclusive participation', 'Extended reach', 'Content longevity', 'Cost-effective engagement'],
    activities: ['Livestreams', 'Virtual chat', 'Interactive polling', 'Digital breakout rooms'],
    image: 'https://images.unsplash.com/photo-1609921212029-bb5a28e60960?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  // New expert trainers data
  const expertCategories = [{
    title: 'Leadership Development',
    icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1614" />,
    description: 'Transform managers into inspiring leaders through immersive workshops and personalized coaching.',
    experts: ['Harvard Business School', 'Wharton', 'Stanford GSB']
  }, {
    title: 'Innovation & Design Thinking',
    icon: <LightbulbIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1615" />,
    description: 'Unlock creative problem-solving with hands-on workshops led by innovation experts.',
    experts: ['IDEO Alumni', 'MIT Media Lab', 'Stanford d.school']
  }, {
    title: 'Strategic Planning',
    icon: <BriefcaseIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1616" />,
    description: 'Develop actionable roadmaps with guidance from experienced strategy consultants and academics.',
    experts: ['McKinsey Alumni', 'London Business School', 'INSEAD']
  }, {
    title: 'Team Effectiveness',
    icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1617" />,
    description: 'Build high-performing teams through evidence-based frameworks and experiential learning.',
    experts: ['Google re:Work Trainers', 'Gallup Certified Coaches', 'Kellogg School']
  }];
  const services = [{
    title: 'Travel Logistics',
    items: ['Room blocks', 'Flight coordination', 'Ground transportation', 'VIP arrivals & departures']
  }, {
    title: 'Event Curation & Design',
    items: ['Custom agendas', 'Brand storytelling', 'Expert trainers', 'Theme development']
  }, {
    title: 'Production & Logistics',
    items: ['Venue consulting', 'Vendor management', 'On-site team', 'Risk management']
  }, {
    title: 'Event Tech',
    items: ['Custom apps', 'AV solutions', 'QR experiences', 'Virtual integrations']
  }, {
    title: 'Wellness & Community',
    items: ['Fitness activities', 'CSR programs', 'Beach clean-ups', 'Reef restoration']
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1618">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-1619">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1620">
          <div className="max-w-3xl" data-id="element-1621">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1622">
              Solutions
            </h1>
            <p className="text-xl text-white/90" data-id="element-1623">
              End-to-end event management tailored to your organization's unique
              goals and culture.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 bg-white" data-id="element-1624">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1625">
          <div className="space-y-24" data-id="element-1626">
            {solutions.map((solution, index) => <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`} data-id="element-1627">
                <div className={index % 2 === 1 ? 'md:col-start-2' : ''} data-id="element-1628">
                  <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1629">
                    {solution.title}
                  </h2>
                  <p className="text-lg text-[#374047]/80 mb-8" data-id="element-1630">
                    {solution.description}
                  </p>
                  <div className="mb-6" data-id="element-1631">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-1632">
                      Outcomes
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1633">
                      {solution.outcomes.map((outcome, i) => <li key={i} className="flex items-start" data-id="element-1634">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1635" />
                          <span className="ml-3 text-[#374047]" data-id="element-1636">{outcome}</span>
                        </li>)}
                    </ul>
                  </div>
                  <div data-id="element-1637">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-1638">
                      Typical Activities
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1639">
                      {solution.activities.map((activity, i) => <li key={i} className="flex items-start" data-id="element-1640">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1641" />
                          <span className="ml-3 text-[#374047]" data-id="element-1642">
                            {activity}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                  <div className="mt-8" data-id="element-1643">
                    <Button variant="outline" to="/contact" data-id="element-1644">
                      Discuss Your {solution.title}
                    </Button>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'md:col-start-1' : ''} data-id="element-1645">
                  <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-1646">
                    <img src={solution.image} alt={solution.title} className="w-full h-full object-cover" data-id="element-1647" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-1648"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Expert Trainers Section - NEW */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1649">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1650">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1651">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1652">
              World-Class Expert Network
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1653">
              Elevate your corporate retreat with specialized training from top
              business school professors and industry experts.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-1654">
            <div data-id="element-1655">
              <h3 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-1656">
                From Boardroom to Classroom to Beach
              </h3>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1657">
                Our exclusive network of professors from the world's top
                business schools and corporate trainers brings unparalleled
                expertise to your retreat experience.
              </p>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1658">
                We carefully match expert facilitators to your specific
                organizational goals, creating custom learning experiences that
                drive real business outcomes.
              </p>
              <div className="space-y-4" data-id="element-1659">
                <div className="flex items-start" data-id="element-1660">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1661" />
                  <span className="ml-3 text-[#374047]" data-id="element-1662">
                    Expert-led workshops integrated into your retreat agenda
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1663">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1664" />
                  <span className="ml-3 text-[#374047]" data-id="element-1665">
                    Immersive learning experiences in inspiring settings
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1666">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1667" />
                  <span className="ml-3 text-[#374047]" data-id="element-1668">
                    Custom content development aligned with your objectives
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1669">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1670" />
                  <span className="ml-3 text-[#374047]" data-id="element-1671">
                    Follow-up implementation support for lasting impact
                  </span>
                </div>
              </div>
            </div>
            <div data-id="element-1672">
              <img src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Expert-led workshop" className="rounded-xl shadow-lg" data-id="element-1673" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1674">
            {expertCategories.map((category, index) => <Card key={index} className="h-full" data-id="element-1675">
                <CardBody className="p-6" data-id="element-1676">
                  <div className="flex flex-col items-center text-center mb-6" data-id="element-1677">
                    <div className="mb-4 mt-2" data-id="element-1678">{category.icon}</div>
                    <h3 className="text-xl font-bold text-[#374047]" data-id="element-1679">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-[#374047]/80 mb-6" data-id="element-1680">
                    {category.description}
                  </p>
                  <div className="mt-auto" data-id="element-1681">
                    <h4 className="text-sm font-semibold text-[#0099A1] mb-2" data-id="element-1682">
                      Featured Experts From:
                    </h4>
                    <ul className="space-y-1" data-id="element-1683">
                      {category.experts.map((expert, i) => <li key={i} className="text-sm text-[#374047]" data-id="element-1684">
                          {expert}
                        </li>)}
                    </ul>
                  </div>
                </CardBody>
              </Card>)}
          </div>
          <div className="mt-12 text-center" data-id="element-1685">
            <Button variant="primary" to="/contact" data-id="element-1686">
              <BookOpenIcon className="h-5 w-5 mr-2" data-id="element-1687" />
              Explore Expert-Led Programs
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-1688">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1689">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1690">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1691">
              Included Services
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1692">
              Every DestaMeet experience includes comprehensive support from our
              expert team.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1693">
            {services.map((service, index) => <Card key={index} className="h-full" data-id="element-1694">
                <CardBody data-id="element-1695">
                  <h3 className="text-xl font-bold text-[#0099A1] mb-4" data-id="element-1696">
                    {service.title}
                  </h3>
                  <ul className="space-y-3" data-id="element-1697">
                    {service.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-1698">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1699" />
                        <span className="ml-3 text-[#374047]" data-id="element-1700">{item}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      <CtaSection data-id="element-1701" />
    </div>;
}